<template>
  <div class="honors-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">{{ pageTitle }}</h1>
        <div class="header-divider"></div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="page-content">
      <div class="container">
        <!-- 加载状态 -->
        <div v-if="isLoading" class="loading-container">
          <div class="loading-spinner"></div>
          <p class="loading-text">{{ t('common.loading') }}</p>
        </div>

        <!-- 错误状态 -->
        <div v-else-if="errorMessage" class="error-container">
          <div class="error-icon">
            <el-icon size="60"><Warning /></el-icon>
          </div>
          <h3 class="error-title">{{ t('common.error') }}</h3>
          <p class="error-message">{{ errorMessage }}</p>
          <button class="retry-button" @click="fetchPageData">
            {{ t('common.retry') }}
          </button>
        </div>

        <!-- 内容区域 -->
        <div v-else class="content-wrapper">
          <!-- 富文本内容（如果有） -->
          <div
            v-if="richtextHtml && richtextHtml.trim()"
            class="richtext-content"
            v-html="richtextHtml"
          ></div>

          <!-- 荣誉图片墙 -->
          <div v-if="imageList && imageList.length > 0" class="images-section">
            <h2 class="section-title">{{ t('honors.certificates') }}</h2>
            <div class="photo-wall">
              <div
                v-for="(image, index) in imageList"
                :key="index"
                class="photo-wall-item"
                @click="openImagePreview(index)"
              >
                <div class="photo-wall-image-container">
                  <img
                    :src="image.path"
                    :alt="image.name"
                    class="photo-wall-image"
                    @error="handleImageError"
                  />
                  <div class="photo-wall-overlay">
                    <el-icon size="32" color="white"><ZoomIn /></el-icon>
                  </div>
                </div>
              </div>
            </div>

            <!-- 图片墙翻页 -->
            <div v-if="imageTotal > imagePageSize" class="image-pagination">
              <el-pagination
                background
                layout="prev, pager, next"
                :total="imageTotal"
                :page-size="imagePageSize"
                :current-page="imagePage"
                @current-change="handleImagePageChange"
              />
            </div>
          </div>

          <!-- 空状态（当没有任何内容时显示） -->
          <div
            v-if="!richtextHtml?.trim() && !imageList?.length"
            class="empty-state"
          >
            <div class="empty-icon">
              <el-icon size="80" color="var(--el-color-primary-light-8)">
                <Trophy />
              </el-icon>
            </div>
            <h3 class="empty-title">{{ t('common.no_data') }}</h3>
            <p class="empty-description">{{ t('honors.no_honors') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 图片预览弹窗 -->
    <el-dialog
      v-model="previewVisible"
      title=""
      width="80%"
      top="5vh"
      class="image-preview-dialog"
      destroy-on-close
    >
      <div class="preview-container">
        <img
          :src="previewImageUrl"
          :alt="t('common.image_preview')"
          class="preview-image"
        />
        <div class="preview-nav">
          <button
            v-if="imageList.length > 1"
            class="preview-arrow prev-arrow"
            @click="prevPreview"
            :disabled="previewIndex === 0"
          >
            <el-icon size="24"><ArrowLeft /></el-icon>
          </button>
          <button
            v-if="imageList.length > 1"
            class="preview-arrow next-arrow"
            @click="nextPreview"
            :disabled="previewIndex === imageList.length - 1"
          >
            <el-icon size="24"><ArrowRight /></el-icon>
          </button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Warning, ArrowLeft, ArrowRight, Trophy, ZoomIn } from '@element-plus/icons-vue'
import { getSinglePageContent, getImageWallList } from "@/assets/api/website";

// 使用 i18n
const { locale, t } = useI18n()

// 响应式数据
const pageData = ref(null)
const isLoading = ref(false)
const errorMessage = ref('')
const imageList = ref([])
const imageTotal = ref(0)
const imagePage = ref(1)
const imagePageSize = ref(9)
const previewVisible = ref(false)
const previewIndex = ref(0)

// 计算属性
const pageTitle = computed(() => {
  return pageData.value?.page_name || t('menu.honors')
})

const richtextHtml = computed(() => {
  return pageData.value?.content?.richtext || ''
})

const previewImageUrl = computed(() => {
  return imageList.value[previewIndex.value]?.path || ''
})

// 图片墙翻页
const handleImagePageChange = (page) => {
  imagePage.value = page
  previewIndex.value = 0
  fetchImageWall()
}

// 图片加载失败处理
const handleImageError = (event) => {
  event.target.style.display = 'none'
  const container = event.target.parentElement
  const placeholder = document.createElement('div')
  placeholder.className = 'image-error-placeholder'
  placeholder.innerHTML = '<p class="placeholder-text">图片加载失败</p>'
  container.appendChild(placeholder)
}

// 图片预览
const openImagePreview = (index) => {
  previewIndex.value = index
  previewVisible.value = true
}

const nextPreview = () => {
  if (previewIndex.value < imageList.value.length - 1) {
    previewIndex.value++
  }
}

const prevPreview = () => {
  if (previewIndex.value > 0) {
    previewIndex.value--
  }
}

// 获取图片墙数据
const fetchImageWall = async () => {
  try {
    const res = await getImageWallList({
      module: 'pizu_single_page',
      path: '/honors',
      page: imagePage.value,
      size: imagePageSize.value
    })
    imageList.value = res?.list || []
    imageTotal.value = res?.total || 0
  } catch (error) {
    console.error('获取荣誉图片墙数据失败:', error)
    imageList.value = []
    imageTotal.value = 0
  }
}

// 获取页面数据
const fetchPageData = async () => {
  isLoading.value = true
  errorMessage.value = ''

  try {
    const currentLocale = locale.value

    const [pageRes] = await Promise.all([
      getSinglePageContent({
        lang: currentLocale,
        path: '/honors'
      }),
      fetchImageWall()
    ])

    if (pageRes && pageRes.content) {
      pageData.value = pageRes
    } else {
      pageData.value = null
    }
  } catch (error) {
    console.error('获取页面数据失败:', error)
    errorMessage.value = t('common.load_failed')
  } finally {
    isLoading.value = false
  }
}

// 监听语言变化，重新加载数据
watch(locale, () => {
  imagePage.value = 1
  fetchPageData()
})

// 组件挂载时获取数据
onMounted(() => {
  fetchPageData()
})
</script>

<style scoped lang="scss">
@import '@/assets/css/common';

.honors-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #f9fbf7 0%, #ffffff 100%);
}

// 页面头部样式
.page-header {
  padding: 200px 0 100px;
  background: url('@/assets/img/header.jpg') center center / cover no-repeat;
  position: relative;
  border-bottom: 1px solid rgba($primary-color, 0.1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: inherit;
    filter: blur(4px);
    z-index: 1;
    transform: scale(1.01);
  }

  & > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding: 140px 0 60px;
  }
}

.page-title {
  font-family: $font-serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
  text-align: center;
  margin-bottom: 20px;
  letter-spacing: 2px;
  text-shadow: 0 1px 2px $primary-color;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.75rem;
    margin-bottom: 16px;
  }
}

.header-divider {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, $primary-color, $secondary-color);
  margin: 0 auto;
  border-radius: 2px;

  @media (max-width: 480px) {
    width: 60px;
    height: 3px;
  }
}

// 主要内容区域
.page-content {
  padding: 60px 0;

  @media (max-width: 768px) {
    padding: 40px 0;
  }

  @media (max-width: 480px) {
    padding: 30px 0;
  }
}

.container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 60px;
}

// 富文本内容样式
.richtext-content {
  background: $white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: $shadow-light;
  border: 1px solid rgba($primary-color, 0.1);
  line-height: 1.8;

  p {
    color: $gray-80;
    font-size: 1.125rem;
    text-align: justify;
    text-indent: 2em;

    &:last-child {
      margin-bottom: 0;
    }
  }

  @media (max-width: 768px) {
    padding: 30px 24px;

    p {
      font-size: 1rem;
    }
  }

  @media (max-width: 480px) {
    padding: 24px 20px;

    p {
      font-size: 0.9375rem;
    }
  }
}

// 图片展示区域
.images-section {
  background: rgba(166, 108, 72, 0.1);
  border-radius: 16px;
  padding: 40px;
  box-shadow: $shadow-light;
  border: 1px solid rgba($primary-color, 0.1);

  @media (max-width: 768px) {
    padding: 30px 24px;
  }

  @media (max-width: 480px) {
    padding: 24px 20px;
  }
}

.section-title {
  text-align: center;
  padding-bottom: 40px;
  font-size: 1.75rem;
  color: $text-color;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    padding-bottom: 30px;
  }

  @media (max-width: 480px) {
    font-size: 1.25rem;
    padding-bottom: 24px;
  }
}

// 照片墙样式
.photo-wall {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;

  @media (max-width: 768px) {
    gap: 16px;
  }
}

.photo-wall-item {
  cursor: pointer;
  transition: transform 0.3s ease;
  width: calc((100% - 40px) / 3);

  &:hover {
    transform: translateY(-4px);
  }

  @media (max-width: 768px) {
    width: calc((100% - 16px) / 2);
  }
}

.photo-wall-image-container {
  position: relative;
  width: 100%;
  aspect-ratio: 4/3;
  border-radius: 12px;
  overflow: hidden;
  background: $gray-10;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(166, 108, 72, 0.15);
}

.photo-wall-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.photo-wall-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba($primary-color, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.photo-wall-item:hover .photo-wall-overlay {
  opacity: 1;
}

.photo-wall-item:hover .photo-wall-image {
  transform: scale(1.05);
}

// 图片墙翻页
.image-pagination {
  display: flex;
  justify-content: center;
  margin-top: 36px;

  @media (max-width: 480px) {
    margin-top: 24px;
  }
}

// 图片加载失败占位符
.image-error-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: $gray-10;
  border-radius: 8px;

  .placeholder-text {
    font-size: 0.875rem;
    color: $gray-60;
  }
}

// 加载状态
.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 3px solid rgba($primary-color, 0.1);
  border-top-color: $primary-color;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

.loading-text {
  font-size: 1rem;
  color: $text-light;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// 错误状态
.error-container {
  text-align: center;
  padding: 60px 20px;

  @media (max-width: 768px) {
    padding: 40px 20px;
  }
}

.error-icon {
  margin-bottom: 20px;
  color: #f56c6c;
}

.error-title {
  font-size: 1.5rem;
  color: #f56c6c;
  margin-bottom: 12px;
  font-weight: 600;

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
}

.error-message {
  font-size: 1rem;
  color: $text-light;
  margin-bottom: 20px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.retry-button {
  background: $primary-color;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: $primary-light-3;
    transform: translateY(-2px);
  }
}

// 空状态
.empty-state {
  text-align: center;
  padding: 80px 20px;
  background: $white;
  border-radius: 16px;
  box-shadow: $shadow-light;
  border: 1px solid rgba($primary-color, 0.1);

  @media (max-width: 768px) {
    padding: 60px 20px;
  }

  @media (max-width: 480px) {
    padding: 40px 16px;
  }
}

.empty-icon {
  margin-bottom: 24px;
  opacity: 0.7;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
}

.empty-title {
  font-size: 1.5rem;
  color: $text-color;
  margin-bottom: 12px;
  font-weight: 600;

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
}

.empty-description {
  font-size: 1rem;
  color: $text-light;
  max-width: 400px;
  margin: 0 auto;
  line-height: 1.5;

  @media (max-width: 480px) {
    font-size: 0.9375rem;
  }
}

// 图片预览弹窗
.preview-container {
  position: relative;
  width: 100%;
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  overflow: hidden;
}

.preview-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}

.preview-nav {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.preview-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  pointer-events: auto;
  z-index: 10;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-50%) scale(1.1);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  &.prev-arrow {
    left: 20px;
  }

  &.next-arrow {
    right: 20px;
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;

    &.prev-arrow {
      left: 10px;
    }

    &.next-arrow {
      right: 10px;
    }
  }
}

:deep(.image-preview-dialog) {
  .el-dialog__header {
    padding: 16px 20px;
    border-bottom: 1px solid #eee;
  }

  .el-dialog__body {
    padding: 0;
  }
}
</style>